import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { 
  updateProfile, 
  sendPasswordResetEmail, 
  EmailAuthProvider, 
  reauthenticateWithCredential 
} from 'firebase/auth';
import { useEffectiveUser } from '../contexts/AuthContext';
import { 
  FaUser, 
  FaEnvelope, 
  FaLock, 
  FaExclamationCircle, 
  FaCheckCircle, 
  FaInfoCircle, 
  FaKey, 
  FaSave, 
  FaSpinner, 
  FaArrowLeft, 
  FaEye, 
  FaEyeSlash,
  FaCrown
} from 'react-icons/fa';
import { doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Membership from '../components/Membership';
import { motion } from 'framer-motion';

function ViewProfile() {
  const navigate = useNavigate();
  const { currentUser, realUser, isImpersonating } = useEffectiveUser();
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [error, setError] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [userHasTyped, setUserHasTyped] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [usernameChecks, setUsernameChecks] = useState({
    checking: false,
    available: true
  });
  // Membership and subscription state
  const [membershipLevel, setMembershipLevel] = useState('free'); // 'free', 'promo', 'premium'
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Initialize cooldown from localStorage on component mount
  useEffect(() => {
    // Get cooldown end time from localStorage
    const resetCooldownEndTime = localStorage.getItem('resetCooldownEndTime');
    
    if (resetCooldownEndTime) {
      const endTime = parseInt(resetCooldownEndTime);
      const now = Date.now();
      
      // If the cooldown hasn't expired yet, calculate remaining time
      if (endTime > now) {
        const remainingSeconds = Math.ceil((endTime - now) / 1000);
        setResetCooldown(remainingSeconds);
      } else {
        // Clear expired cooldown
        localStorage.removeItem('resetCooldownEndTime');
      }
    }
  }, []);

  // Fetch user's membership level and subscription data
  useEffect(() => {
    if (!currentUser) return;

    // Listen to user document for membership level
    const unsubscribeUser = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setMembershipLevel(userData.membershipLevel || 'free');
        }
      },
      (error) => {
        console.error('Error fetching user data:', error);
      }
    );

    // Listen to subscription document
    const unsubscribeSubscription = onSnapshot(
      doc(db, 'userSubscriptions', currentUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setSubscriptionData(docSnapshot.data());
        } else {
          setSubscriptionData(null);
        }
      },
      (error) => {
        console.error('Error fetching subscription data:', error);
      }
    );

    return () => {
      unsubscribeUser();
      unsubscribeSubscription();
    };
  }, [currentUser]);

  // Cooldown timer effect
  useEffect(() => {
    let timer;
    if (resetCooldown > 0) {
      timer = setTimeout(() => {
        const newCooldown = resetCooldown - 1;
        setResetCooldown(newCooldown);
        
        // Clear localStorage when cooldown expires
        if (newCooldown === 0) {
          localStorage.removeItem('resetCooldownEndTime');
        }
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resetCooldown]);

  const checkUsernameAvailability = async (username) => {
    // If it's the same as current username, no need to check
    if (username === currentUser?.displayName) {
      setUsernameChecks({ checking: false, available: true });
      return;
    }
    
    if (!username) {
      setUsernameChecks({ checking: false, available: false });
      return;
    }
    
    try {
      setUsernameChecks({ checking: true, available: false });
      const lowercaseUsername = username.toLowerCase();
      
      // Check in users collection
      const usersRef = collection(db, 'users');
      const usernameUserQuery = query(usersRef, where('usernameLower', '==', lowercaseUsername));
      const usernameUserSnapshot = await getDocs(usernameUserQuery);
      
      // Check in pendingRegistrations collection
      const pendingRef = collection(db, 'pendingRegistrations');
      const usernamePendingQuery = query(pendingRef, where('usernameLower', '==', lowercaseUsername));
      const usernamePendingSnapshot = await getDocs(usernamePendingQuery);
      
      const isAvailable = usernameUserSnapshot.empty && usernamePendingSnapshot.empty;
      setUsernameChecks({ checking: false, available: isAvailable });
    } catch (error) {
      setUsernameChecks({ checking: false, available: false });
    }
  };

  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    setError('');

    if (isImpersonating) {
      toast.error('Cannot update username while impersonating');
      return;
    }

    if (!password) {
      toast.error('Please enter your password to confirm changes');
      return;
    }

    if (!usernameChecks.available) {
      toast.error('Username is not available');
      return;
    }

    try {
      setUsernameLoading(true);
      
      // Verify current password by attempting to reauthenticate
      try {
        const credential = EmailAuthProvider.credential(
          realUser.email, // Use realUser for auth operations
          password
        );
        await reauthenticateWithCredential(realUser, credential);
      } catch (error) {
        console.error('Reauthentication error:', error);
        toast.error('Incorrect password');
        setUsernameLoading(false);
        return;
      }
      
      // If we get here, password was correct, proceed with update
      await updateProfile(realUser, {
        displayName: newUsername
      });

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: newUsername,
        username: newUsername,
        usernameLower: newUsername.toLowerCase()
      });
      
      toast.success('Username updated successfully!');
      setUserHasTyped(false);
    } catch (error) {
      console.error('Error updating username:', error);
      toast.error('Failed to update username');
    } finally {
      setUsernameLoading(false);
      setPassword('');
    }
  };

  const handleResetPassword = async () => {
    if (isImpersonating) {
      toast.error('Cannot reset password while impersonating');
      return;
    }

    try {
      setError('');
      setPasswordLoading(true);
      await sendPasswordResetEmail(auth, realUser.email);
      
      // Set 60 second cooldown
      const cooldownSeconds = 60;
      setResetCooldown(cooldownSeconds);
      
      // Store cooldown end time in localStorage
      const endTime = Date.now() + (cooldownSeconds * 1000);
      localStorage.setItem('resetCooldownEndTime', endTime.toString());
      
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to send password reset email');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle username input change
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setNewUsername(value);
    setUserHasTyped(true);
    checkUsernameAvailability(value);
  };

  // Determine if update button should be disabled
  const isUpdateDisabled = usernameLoading || 
    usernameChecks.checking ||
    !usernameChecks.available || 
    newUsername === currentUser?.displayName || 
    !newUsername;

  // Handle Stripe Customer Portal for subscription management
  const handleManageSubscription = async () => {
    if (isImpersonating) {
      toast.error('Cannot manage subscription while impersonating');
      return;
    }

    setSubscriptionLoading(true);
    try {
      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortalSession();
      
      if (result.data && result.data.url) {
        // Open Stripe Customer Portal in new tab
        window.open(result.data.url, '_blank');
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      
      if (error.code === 'functions/not-found') {
        toast.error('No active subscription found');
      } else if (error.code === 'functions/unauthenticated') {
        toast.error('Please log in to manage subscription');
      } else {
        toast.error('Failed to open subscription management. Please try again.');
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="flex items-center mb-4 sm:mb-6"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <FaArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Profile</h1>
        </motion.div>

        {/* Profile Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full p-4">
                <FaUser className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Profile Settings</h2>
                <p className="text-gray-500 dark:text-gray-400">Manage your account information</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Left Column - Account Info and Username Update */}
          <div className="lg:col-span-3 space-y-4">
            {/* Account Info Section */}
            <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Account Information</h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center">
                    <FaEnvelope className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Email Address</p>
                      <p className="text-gray-900 dark:text-white text-sm">{currentUser?.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Username Update Section */}
              <form onSubmit={handleUpdateUsername} className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Change Username</h3>
                <div className="space-y-3">
                  {/* Current Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Current Username
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={currentUser?.displayName || ''}
                        className="pl-10 w-full border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-2 text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        readOnly
                      />
                    </div>
                  </div>

                  {/* New Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      New Username
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={newUsername}
                        onChange={handleUsernameChange}
                        className="pl-10 w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter new username"
                      />
                    </div>
                    {userHasTyped && newUsername && newUsername !== currentUser?.displayName && (
                      <div className="mt-1">
                        {usernameChecks.checking ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Checking availability...</p>
                        ) : (
                          <p className={`text-xs flex items-center ${usernameChecks.available ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {usernameChecks.available ? '✓ Username is available' : '✗ Username is not available'}
                          </p>
                        )}
                      </div>
                    )}
                    {userHasTyped && newUsername === currentUser?.displayName && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        New username is the same as current username
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                      >
                        {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Required to confirm this change
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isUpdateDisabled}
                    className="w-full flex items-center justify-center space-x-2 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors text-sm"
                  >
                    {usernameLoading ? (
                      <>
                        <FaSpinner className="animate-spin h-4 w-4" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <FaSave className="h-4 w-4" />
                        <span>Update Username</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Password Reset Section */}
            <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Password Settings</h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-3">
                  <div className="flex items-start space-x-2">
                    <FaInfoCircle className="h-4 w-4 text-gray-400 dark:text-gray-500 mt-0.5" />
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Need to change your password? Click below to receive a password reset link via email.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleResetPassword}
                  disabled={passwordLoading || resetCooldown > 0}
                  className="w-full flex items-center justify-center space-x-2 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors text-sm"
                >
                  {passwordLoading ? (
                    <>
                      <FaSpinner className="animate-spin h-4 w-4" />
                      <span>Sending...</span>
                    </>
                  ) : resetCooldown > 0 ? (
                    <>
                      <FaSpinner className="h-4 w-4" />
                      <span>Try again in {resetCooldown}s</span>
                    </>
                  ) : (
                    <>
                      <FaKey className="h-4 w-4" />
                      <span>Send Password Reset Email</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Subscription Management Section - Only show for premium users */}
            {membershipLevel === 'premium' && subscriptionData && (
              <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Subscription Management</h3>
                  <div className={`${subscriptionData.cancelAtPeriodEnd ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-amber-50 dark:bg-amber-900/20'} rounded-lg p-3 mb-3`}>
                    <div className="flex items-start space-x-2">
                      <FaCrown className={`h-4 w-4 ${subscriptionData.cancelAtPeriodEnd ? 'text-orange-600 dark:text-orange-400' : 'text-amber-600 dark:text-amber-400'} mt-0.5`} />
                      <div className="flex-1">
                        <p className="text-xs text-gray-900 dark:text-white font-medium">
                          Advanced Premium - {subscriptionData.cancelAtPeriodEnd ? 'Cancelled' : 'Active'}
                        </p>
                        {subscriptionData.currentPeriodEnd && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {subscriptionData.cancelAtPeriodEnd 
                              ? `Access until: ${new Date(subscriptionData.currentPeriodEnd.seconds * 1000).toLocaleDateString()}`
                              : `Next billing: ${new Date(subscriptionData.currentPeriodEnd.seconds * 1000).toLocaleDateString()}`
                            }
                          </p>
                        )}
                        {subscriptionData.cancelAtPeriodEnd && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            Your subscription has been cancelled and will not renew.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleManageSubscription}
                    disabled={subscriptionLoading}
                    className="w-full flex items-center justify-center space-x-2 bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 transition-colors text-sm"
                  >
                    {subscriptionLoading ? (
                      <>
                        <FaSpinner className="animate-spin h-4 w-4" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <FaCrown className="h-4 w-4" />
                        <span>Manage Subscription</span>
                      </>
                    )}
                  </button>
                  <p className="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
                    Update payment method, cancel or view invoices
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Membership Section */}
          <div className="lg:col-span-7">
            <Membership membershipLevel={membershipLevel} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewProfile;